import { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return url;
};

export default function ImageCarousel({ images = [], videoUrl, className = '' }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  const allMedia = [];
  if (videoUrl) allMedia.push({ type: 'video', url: videoUrl });
  images.forEach(img => allMedia.push({ type: 'image', url: img }));

  const scrollTo = useCallback((index) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: scrollRef.current.clientWidth * index, behavior: 'smooth' });
      setActiveIndex(index);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const idx = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
      setActiveIndex(idx);
    }
  }, []);

  if (allMedia.length === 0) {
    return <div className={`bg-zinc-900 flex items-center justify-center ${className}`}>
      <span className="text-muted-foreground font-body text-sm">Sin media</span>
    </div>;
  }

  return (
    <div className={`relative ${className}`} data-testid="image-carousel">
      <div ref={scrollRef} onScroll={handleScroll} className="w-full h-full overflow-x-scroll snap-x snap-mandatory scrollbar-hide flex">
        {allMedia.map((media, i) => (
          <div key={i} className="w-full h-full shrink-0 snap-start">
            {media.type === 'video' ? (
              <video src={getMediaUrl(media.url)} className="object-cover w-full h-full" loop muted playsInline controls />
            ) : (
              <img src={getMediaUrl(media.url)} className="object-cover w-full h-full" alt="" />
            )}
          </div>
        ))}
      </div>
      {allMedia.length > 1 && (
        <>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {allMedia.map((_, i) => (
              <button key={i} onClick={() => scrollTo(i)} className={`h-1.5 rounded-full transition-all ${i === activeIndex ? 'bg-primary w-5' : 'bg-white/50 w-1.5'}`} />
            ))}
          </div>
          <button onClick={() => scrollTo(Math.max(0, activeIndex - 1))} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 items-center justify-center rounded-sm z-10">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button onClick={() => scrollTo(Math.min(allMedia.length - 1, activeIndex + 1))} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 items-center justify-center rounded-sm z-10">
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </>
      )}
    </div>
  );
}
